import { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import ShippingMethodForm from '../components/ShippingMethodForm';
import PageHeader from '../components/ui/PageHeader';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';

export default function ShippingMethods() {
  const [shippingMethods, setShippingMethods] = useState([]);
  const [filteredMethods, setFilteredMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingMethods, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const methodsResponse = await api.get('/auth/shipping-methods');
      setShippingMethods(methodsResponse.data.data);
    } catch (err) {
      setError('Failed to fetch shipping methods');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...shippingMethods];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(m =>
        m.carrierName?.toLowerCase().includes(search) ||
        m.key?.toLowerCase().includes(search) ||
        m.name?.default?.toLowerCase().includes(search)
      );
    }

    // Sort by displayOrder
    filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    setFilteredMethods(filtered);
  };

  const handleAdd = () => {
    setEditingMethod(null);
    setShowForm(true);
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setShowForm(true);
  };

  const handleDelete = async (method) => {
    if (!window.confirm(`Are you sure you want to delete "${method.name?.default || method.carrierName}"?`)) {
      return;
    }

    try {
      await api.delete(`/auth/shipping-methods/${method._id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete shipping method');
      console.error(err);
    }
  };

  const handleSave = async (data) => {
    if (editingMethod) {
      await api.patch(`/auth/shipping-methods/${editingMethod._id}`, data);
    } else {
      await api.post('/auth/shipping-methods', data);
    }
    setShowForm(false);
    fetchData();
  };

  const handleSetDefault = async (method) => {
    try {
      await api.patch(`/auth/shipping-methods/${method._id}/set-default`);
      fetchData();
    } catch (err) {
      alert('Failed to set as default');
      console.error(err);
    }
  };

  const handleReorder = async (affectedItems) => {
    try {
      await api.patch('/auth/shipping-methods/reorder', affectedItems);
      const updatedMethods = shippingMethods.map(method => {
        const affected = affectedItems.find(item => item.id === method._id);
        if (affected) {
          return { ...method, displayOrder: affected.displayOrder };
        }
        return method;
      });
      setShippingMethods(updatedMethods);
    } catch (err) {
      alert('Failed to reorder shipping methods');
      console.error(err);
      fetchData();
    }
  };

  const formatDeliveryDays = (estimatedDeliveryDays) => {
    if (!estimatedDeliveryDays) return '-';
    const { min, max } = estimatedDeliveryDays;
    if (min === max) return `${min} day${min === 1 ? '' : 's'}`;
    return `${min}-${max} days`;
  };

  const formatThreshold = (thresholdInCents) => {
    if (!thresholdInCents) return 'Disabled';
    return `€${(thresholdInCents / 100).toFixed(2)}`;
  };

  const columns = [
    {
      key: 'isDefault',
      label: 'Default',
      render: (val, row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!val) handleSetDefault(row);
          }}
          className={`p-1 rounded transition-colors ${val ? 'text-yellow-500' : 'text-slate-300 hover:text-yellow-400'}`}
          title={val ? 'Default shipping method' : 'Set as default'}
        >
          {val ? <StarIcon className="h-5 w-5" /> : <StarOutlineIcon className="h-5 w-5" />}
        </button>
      )
    },
    { key: 'carrierName', label: 'Carrier' },
    {
      key: 'name',
      label: 'Name',
      render: (val) => val?.default || '-'
    },
    { key: 'serviceLevel', label: 'Service Level' },
    {
      key: 'estimatedDeliveryDays',
      label: 'Delivery Time',
      render: (val) => formatDeliveryDays(val)
    },
    {
      key: 'pricingTiers',
      label: 'Tiers',
      render: (val) => val?.length ? `${val.length} tier${val.length > 1 ? 's' : ''}` : '-'
    },
    {
      key: 'countries',
      label: 'Countries',
      render: (val) => val?.join(', ') || 'PT'
    },
    {
      key: 'freeShippingThresholdInCents',
      label: 'Free Shipping',
      render: (val) => formatThreshold(val)
    },
    { key: 'displayOrder', label: 'Order' }
  ];

  const renderFilters = () => (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        placeholder="Search by carrier, name or key..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading shipping methods...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
      {error}
    </div>
  );

  return (
    <div>
      <PageHeader title="Shipping Methods" description="Manage shipping carriers and rates" />
      {showForm ? (
        <ShippingMethodForm
          method={editingMethod}
          onSave={handleSave}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <DataTable
          data={filteredMethods}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          renderFilters={renderFilters}
          reorderable={true}
          onReorder={handleReorder}
        />
      )}
    </div>
  );
}
